import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Cambridge = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/where-to-live-in-toronto/'

  const cheapestLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/cheapest-places-to-rent-greater-toronto-area/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.cambridge-on:About Cambridge Paragraph 1">
            Situated in Southern{' '}
            <LinkText href="/rental-listings/ontario">Ontario</LinkText>,
            Cambridge is a fusion of historic charm and contemporary living,
            formed from the amalgamation of three former towns - Galt, Preston,
            and Hespeler, each area retaining its unique allure and historical
            significance. The city’s architectural heritage is evident in its
            preserved buildings and cultural landmarks, offering a glimpse into
            its storied past. Cradled by the banks of the Grand River, an area
            designated a Canadian Heritage River, Cambridge boasts abundant
            natural spaces, providing residents with a host of outdoor pursuits
            such as hiking, fishing, and kayaking.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.cambridge-on:About Cambridge Paragraph 2">
            Cambridge prides itself on a strong sense of community, evidenced
            through its numerous local festivals, farmers’ markets, and social
            events that knit residents together. The city’s arts and culture
            landscape is lively, bolstered by various galleries, theatres, and
            public art installations. While it is home to several educational
            campuses like Conestoga College, it’s important to note that
            institutions like{' '}
            <LinkText href="/rental-listings/city/waterloo/wilfrid-laurier-university">
              Wilfrid Laurier University
            </LinkText>{' '}
            are based in nearby Waterloo, though their influence is felt in the
            academic vigor of the region. Cambridge supports a multifaceted
            economy with robust sectors ranging from advanced manufacturing to
            the tech industry, attracting professionals and families alike. Its
            strategic location within the Waterloo Region enhances the suburban
            appeal, granting residents the advantage of a tranquil lifestyle
            paired with easy access to metropolitan amenities and the broader
            economic dynamism of Southern Ontario.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,500 - $2,500',
          bed2: '$2,000 - $3,000',
          bed3: '$2,500 - $3,500'
        }}
        rentReportCity={t('cities:city_name_toronto')}
        rentReportLink="https://liv.rent/blog/rent-reports/toronto/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e5%a4%9a%e4%bc%a6%e5%a4%9a/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.cambridge-on:Apartment Hunting in Cambridge Paragraph 1">
            {{ cityName }} is an attractive city of Ontario known for its
            thriving community and diverse housing options. While rental prices
            in {{ cityName }} are generally more affordable compared to Toronto,
            they have been steadily increasing over time. As a result, finding{' '}
            <LinkText href={links.under1000}>
              cheap rentals in {{ cityName }}
            </LinkText>
            , especially{' '}
            <LinkText href={links.apartmentsUnder1000}>
              apartments for rent under $1000
            </LinkText>
            , can be challenging. However, to initiate your search, you can
            refer to this guide highlighting{' '}
            <LinkText href={cheapestLink}>
              affordable neighborhoods in the Greater Toronto Area (GTA)
            </LinkText>{' '}
            that may offer relatively more budget-friendly rental options.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cambridge-on:Apartments for rent Cambridge Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Cambridge
              </LinkText>{' '}
              are particularly popular among young professionals, students, and
              smaller families, primarily due to their affordability compared to
              larger standalone houses. They are often located in areas with
              convenient access to amenities and public transportation. The
              pricing can vary significantly based on the unit’s size,
              condition, and location within Cambridge.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cambridge-on:Houses for rent Cambridge Paragraph">
              Full{' '}
              <LinkText href={links.house}>
                houses for rent in Cambridge
              </LinkText>{' '}
              are available, appealing primarily to larger families looking for
              additional space, a yard, and a sense of permanency. The rental
              costs for houses are generally higher due to the added space and
              privacy. These are suitable for long-term renters with stable
              incomes or who are seeking roommates.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent CITY Paragraph">
              Townhouses are a popular form of housing in {{ cityName }} that
              combines the best qualities of both apartments and whole houses.
              Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in {{ cityName }}
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph">
              For those looking to find affordable rental housing in this
              increasingly expensive city, basements for rent in {{ cityName }}{' '}
              pose an attractive alternative. Since house prices are so
              expensive, homeowners will sometimes rent out their basements as a
              way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cambridge-on:Condos for rent Cambridge Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Cambridge
              </LinkText>{' '}
              are a popular choice for those who seek a more upscale lifestyle
              with amenities like fitness centers, communal spaces, and
              sometimes, pools. They tend to be pricier than standard apartments
              and are often found in areas with easy access to city features,
              appealing to professionals and retirees who seek a
              low-maintenance, comfortable lifestyle.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cambridge-on:Short term rentals Cambridge Paragraph">
              Cambridge, Ontario, holds a variety of employment possibilities
              and is in proximity to educational institutions, factors that
              contribute to the demand for housing, including{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText>.
              While there are short-term rental options, especially noticeable
              during transitional periods like the start of an academic term,
              they aren’t as prevalent as long-term accommodations due to the
              city’s more stable residential nature. The option for short-term
              leases may appeal to students, transient workers, and those
              seeking flexibility; however, availability is subject to local
              regulations and market conditions, which potential renters should
              consider when{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are suitable
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION 2 Paragraph">
              {{ cityName }} is known for its inexpensive rent, making it
              relatively easy to find something within your budget. If you’re
              looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in {{ cityName }}{' '}
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.cambridge-on:Living in Cambridge Paragraph">
          Living in Cambridge, Ontario, offers a unique experience that combines
          historical richness with contemporary living. The city is composed of
          three historically distinct areas—Galt, Preston, and Hespeler—each
          boasting its own unique charm and heritage landmarks. The Grand River
          enhances the city’s beauty, providing a backdrop for various
          recreational activities in the area’s extensive park system. Cambridge
          takes pride in its sense of community, evident through its numerous
          festivals and cultural events, while educational pursuits are
          supported by institutions such as Conestoga College. Although not
          directly part of the{' '}
          <LinkText href="/rental-listings/city/waterloo">Waterloo</LinkText>{' '}
          region, Cambridge’s close vicinity allows residents to enjoy a wider
          array of opportunities, maintaining a lifestyle that appreciates both
          historical legacy and modern conveniences. For more insights into
          living costs, prospective residents can refer to this guide on{' '}
          <LinkText href="https://liv.rent/blog/renters/cost-of-living-comparison-in-canadian-cities/">
            Canada’s Top Cities Cost of Living Comparison
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.cambridge-on:Popular Neighbourhoods Cambridge Paragraph">
          Cambridge’s excellent location makes it a popular desitination for
          commuters. For prime access to the 401, renters should look at
          Hespeler/Hespeler Village or Preston Centre. Downtown Cambridge, in
          what used to be known as the Galt municipality, offers great
          convenience and walkability, as well as great views and plenty of
          stunning historical buildings. For families looking to move to
          Cambridge, take a look at East Hespeler for its great school system
          and recreational facilities.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.cambridge-on:Local Landmarks Cambridge Paragraph">
          Cambridge, Ontario, is a treasure trove of historical and natural
          landmarks, offering residents and visitors a glimpse into the city’s
          rich heritage and scenic beauty. One of the standout historical sites
          is the Cambridge Mill, which, aside from its historical significance,
          offers breathtaking views of the Grand River. Not far from the city
          center, the rare Charitable Research Reserve is an expanse of
          conservation land, showcasing local flora and fauna, and offering
          trails for nature enthusiasts. The city’s industrial past is honored
          at the Hespeler Heritage Centre, preserving the vibrant history and
          culture of the Hespeler community. Additionally, the iconic bridges
          such as the Main Street Bridge in Galt add to the city’s historical
          landscape, making Cambridge a unique blend of Ontario’s natural and
          industrial history.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Cambridge
